// Port of Flutter's fanTouchScreen.dart (DeviceID 53).
//
// A 270° dial with the gap at the BOTTOM — it starts at 135° (down-left) and
// sweeps clockwise to 45° (down-right) — with the spinning blades in the middle
// and the speed readout beneath the hub.
//
// Two behaviours worth keeping in mind, both lifted from the Dart:
//
//  • Dragging moves the UI ONLY. Exactly one command is published, on release.
//    The older slider published on every drag tick and machine-gunned the device
//    with MQTT frames for a single gesture.
//  • The hub area (inner 22%) is dead to touch, so grabbing the fan graphic
//    itself doesn't fling the speed.
//
// Long-pressing the body opens the schedule for output 1, as in the Dart.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import SpinningFan from './components/SpinningFan';
import useLongPress from '../../components/useLongPress';
import { useApp } from '../../../../core/state/AppContext';
import { publishCommand } from '../../../../core/utils/deviceController';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../../../../core/mqtt/mqttClient';
import { fetchSeedStatus } from '../../../../core/devices/deviceStatus';
import {
  FAN_SPEEDS, fanCommand, fanFraction, fanStatusLabel, fanStepLabel, parseFanSpeed,
} from '../../../../core/devices/fan';

const DIAL_START = (3 * Math.PI) / 4;   // 135°
const DIAL_SWEEP = (3 * Math.PI) / 2;   // 270°

// SVG arc from `from` to `from + sweep` on a circle of radius r centred at c.
function arcPath(cx, cy, r, from, sweep) {
  const x1 = cx + r * Math.cos(from);
  const y1 = cy + r * Math.sin(from);
  const x2 = cx + r * Math.cos(from + sweep);
  const y2 = cy + r * Math.sin(from + sweep);
  const large = sweep > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

export default function FanScreen({ device }) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId } = useApp();
  const uno = device?.UniqueNumber;

  const [speed, setSpeed] = useState(FAN_SPEEDS[0]);
  const [known, setKnown] = useState(false);
  const [dragSpeed, setDragSpeed] = useState(null);
  const boxRef = useRef(null);

  const displaySpeed = dragSpeed ?? speed;

  // Seed from the server, then follow MQTT.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    fetchSeedStatus(uno).then((raw) => {
      const s = parseFanSpeed(raw);
      if (alive && s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { alive = false; };
  }, [uno]);

  useEffect(() => {
    if (!uno) return undefined;
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uno)) return;
      const s = parseFanSpeed(payload);
      if (s != null) { setSpeed(s); setKnown(true); }
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uno]);

  const box = useMemo(() => {
    const availW = window.innerWidth;
    const availH = window.innerHeight - 64;
    return Math.min(380, Math.max(220, Math.min(availW * 0.82, availH * 0.62)));
  }, []);

  const statusColour = !known ? '#2196F3' : (speed > 10 ? '#66FF00' : '#F44336');
  const running = known && displaySpeed > 10;
  const frac = fanFraction(displaySpeed);

  // Map a touch inside the dial to a speed (drag only — nothing published here).
  const handleTouch = useCallback((clientX, clientY) => {
    const el = boxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = clientX - rect.left - box / 2;
    const dy = clientY - rect.top - box / 2;
    if (Math.hypot(dx, dy) < box * 0.22) return; // dead hub

    let t = (Math.atan2(dy, dx) - DIAL_START) % (2 * Math.PI);
    if (t < 0) t += 2 * Math.PI;
    if (t > DIAL_SWEEP) {
      // In the gap at the bottom — snap to whichever end is nearer.
      t = (t - DIAL_SWEEP) < (2 * Math.PI - t) ? DIAL_SWEEP : 0;
    }
    const idx = Math.round((t / DIAL_SWEEP) * (FAN_SPEEDS.length - 1));
    setDragSpeed(FAN_SPEEDS[Math.min(FAN_SPEEDS.length - 1, Math.max(0, idx))]);
  }, [box]);

  // Publish the speed the finger landed on — exactly one message per gesture.
  const commit = useCallback(() => {
    if (dragSpeed == null) return;
    setSpeed(dragSpeed);
    setKnown(true);
    publishCommand(uno, fanCommand(dragSpeed), userId);
    setDragSpeed(null);
  }, [dragSpeed, uno, userId]);

  const bodyPress = useLongPress(
    () => navigate(`/schedule/${uno}/1`, { state: { outputName: device?.DeviceName, device } }),
    null,
  );

  const r = box / 2;
  const stroke = box * 0.055;
  const radius = (box - stroke) / 2 - box * 0.02;
  const knobAngle = DIAL_START + DIAL_SWEEP * frac;

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box
        {...bodyPress}
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', p: 2,
        }}
      >
        <Box
          ref={boxRef}
          sx={{ width: box, height: box, position: 'relative', touchAction: 'none' }}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleTouch(e.clientX, e.clientY); }}
          onPointerMove={(e) => { if (e.buttons) handleTouch(e.clientX, e.clientY); }}
          onPointerUp={commit}
          onPointerCancel={commit}
        >
          <svg width={box} height={box} style={{ position: 'absolute', inset: 0 }}>
            {/* Track */}
            <path
              d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP)}
              stroke={`${c.text}2E`} strokeWidth={stroke} strokeLinecap="round" fill="none"
            />
            {/* Progress — soft glow under the solid arc. */}
            {frac > 0 && (
              <>
                <path
                  d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP * frac)}
                  stroke={running ? c.button : '#9E9E9E'} strokeWidth={stroke * 1.9}
                  strokeLinecap="round" fill="none" opacity={0.35}
                  style={{ filter: 'blur(8px)' }}
                />
                <path
                  d={arcPath(r, r, radius, DIAL_START, DIAL_SWEEP * frac)}
                  stroke={running ? c.button : '#9E9E9E'} strokeWidth={stroke}
                  strokeLinecap="round" fill="none"
                />
              </>
            )}
            {/* Knob */}
            <circle
              cx={r + radius * Math.cos(knobAngle)} cy={r + radius * Math.sin(knobAngle)}
              r={stroke * 0.85} fill="rgba(255,255,255,0.95)"
            />
            <circle
              cx={r + radius * Math.cos(knobAngle)} cy={r + radius * Math.sin(knobAngle)}
              r={stroke * 0.52} fill={statusColour}
            />
          </svg>

          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
          }}
          >
            <SpinningFan
              size={box * 0.58}
              speedFraction={running ? frac : 0}
              bladeColor={c.button}
              idleColor="#757575"
              hubColor={statusColour}
            />
          </Box>

          <Box sx={{
            position: 'absolute', bottom: box * 0.14, left: 0, right: 0,
            textAlign: 'center', pointerEvents: 'none',
          }}
          >
            <Typography sx={{
              fontSize: Math.min(44, Math.max(20, box * 0.13)),
              fontWeight: 700, color: c.text,
            }}
            >
              {fanStepLabel(displaySpeed)}
            </Typography>
            <Typography sx={{
              fontSize: Math.min(18, Math.max(12, box * 0.052)),
              color: `${c.text}BF`, letterSpacing: '1.1px',
            }}
            >
              {known ? fanStatusLabel(displaySpeed) : 'Unknown'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: box * 0.05 }} />
        <Typography sx={{
          fontSize: Math.min(15, Math.max(11, box * 0.045)), color: `${c.text}99`,
        }}
        >
          Drag around the dial to set speed
        </Typography>
      </Box>
    </Box>
  );
}
